/**
 * Custom agreement data for specific agreement ID: d5c43cd7-ccc8-4eb6-bf79-9a8594e688b8
 * This agreement allows for up to five properties instead of the standard single property.
 */

// Import the base data to use as foundation and only override specific items
import { scheduleItems as baseScheduleItems, termsAndConditions as baseTermsAndConditions } from './agreementData';

// Target agreement ID that should use this custom data
export const CUSTOM_AGREEMENT_ID = 'd5c43cd7-ccc8-4eb6-bf79-9a8594e688b8';

// Custom schedule items - only the ones that need to be modified
export const customScheduleOverrides = {
  4: {
    id: 4,
    title: "Retainer",
    content: "means the sum of $XX (inclusive of GST), in respect of 5 Approved Acquisitions"
  },
  5: {
    id: 5,
    title: "Success Fee",
    content: "means the sum of $XX (inclusive of GST) per Approved Acquisition"
  },
  6: {
    id: 6,
    title: "Price Limit",
    content: "means the following price that you have nominated as the maximum purchase price for a property that we source for you:\n\nTBC"
  },
  8: {
    id: 8,
    title: "Property",
    content: "means any real property (up to five) within the Price Limit and Property Details sourced by Search Property and presented to you under this Agreement."
  },
  9: {
    id: 9,
    title: "Approved Acquisition",
    content: "means the Property that you elect to purchase (up to five in total under this Agreement) with such election made in writing to Search Property or the Agent or a representative of either Search Property and / or the Agent."
  },
  10: {
    id: 10,
    title: "Term",
    content: "means the period of commencing from the date that this Agreement is signed by you and ends on the earlier of:\n\ni. 12 months from that date; or\n\nii. the Settlement Date of the final Approved Acquisition (up to five in total)."
  },
  11: {
    id: 11,
    title: "Services",
    content: "means all work that we conduct for and on your behalf under this Agreement, including but not limited to the following:\n\ni. identify, evaluate and share with you data, information and details about Properties (up to five) that fit the Property Details;\n\nii. discuss each Property with you and answer any questions you have;\n\niii. repeat steps (i) and (ii) until the Buyer is prepared to proceed with purchasing up to five Properties;\n\niv. negotiate sale price with the sales agent / vendor of the Property;\n\nv. assist the Buyer in engaging, briefing and co-ordinating third parties for purchase and settlement of the Property;\n\nvi. liaise with your third party representatives (e.g. conveyancer, mortgage broker, insurance broker) to ensure that the settlement process is as seamless as possible."
  }
};

// Custom terms and conditions - only the sections that need to be modified
export const customTermsOverrides = {
  0: { // Index 0 corresponds to "1. Interpretation and Definitions"
    title: "1. Interpretation and Definitions",
    content: [
      "In this Agreement:",
      "1.1. capitalised terms are defined in accordance with the Schedule;",
      "1.2. where a party to this Agreement comprises of two or more persons, the rights and obligations of those persons pursuant to this Agreement will apply to each of them jointly and severally; and",
      "1.3. all amounts referred to in this Agreement are in Australian Dollars.",
      "1.4. For clarity, this Agreement applies to up to five (5) Approved Acquisitions during the Term. All references to 'Property' or 'Approved Acquisition' in this Agreement shall be read accordingly."
    ]
  },
  4: { // Index 4 corresponds to "5. Payment"
    title: "5. Payment",
    content: [
      "5.1. The Buyer must pay the Retainer at the time of signing this Agreement. Search Property will not undertake any Services until the Retainer is paid.",
      "The Retainer applies to the Agreement as a whole and covers up to five Approved Acquisitions.",
      "5.2. The Success Fee is only payable on settlement of each Approved Acquisition. Search Property will issue an invoice for payment of the Success Fee at the time of settlement of each Approved Acquisition and the Success Fee must be paid within 7 days of the invoice date."
    ]
  }
};

// Function to get custom schedule items by merging base items with overrides
export const getCustomScheduleItems = () => {
  return baseScheduleItems.map(item => {
    if (customScheduleOverrides[item.id as keyof typeof customScheduleOverrides]) {
      return {
        ...item,
        ...customScheduleOverrides[item.id as keyof typeof customScheduleOverrides]
      };
    }
    return item;
  });
};

// Function to get custom terms and conditions by merging base terms with overrides
export const getCustomTermsAndConditions = () => {
  return baseTermsAndConditions.map((section, index) => {
    if (customTermsOverrides[index as keyof typeof customTermsOverrides]) {
      return {
        ...section,
        ...customTermsOverrides[index as keyof typeof customTermsOverrides]
      };
    }
    return section;
  });
};